import moment from "moment";
import { formatDate, formatUTCDate, getNextDay, toUtcDay, getPreviousDayForGivenDate, firstBusinessDayOfNextYear, formatTodaysDate, formatNumber } from "common/formatters";
import TypedSelect from "common/ui/TypedSelect";
import { DeliveryMethod } from "domain/deliveryMethod";
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import Contract, { ContractType } from "domain/contract";
import PhysicalLocation, { getPhysicalLocation } from "domain/physicalLocation";
import { getProducerDisplayName } from "features/settings/producers/producer";
import React, { useState } from "react";
import { Row, Col, Form, InputGroup, Collapse } from 'react-bootstrap';
import { Trader } from "features/settings/traders/trader";
import NumberEditor from "common/ui/NumberEditor";
import { PaymentOption } from "domain/paymentOption";
import FuturesContract from "domain/futuresContract";
import producerService from "features/settings/producers/producerService";
import NP from "number-precision";
import { getPriceLabelForContract } from "common/formatters/strings";
import { useCustomerWillSellText } from "common/formatters/useCustomerWillSellText";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { getTotalFees, isCashContractWithOpenStorage } from "./helpers";
import ContractSubType from "./contractSubType";
import contractService from "./contractService";
import { getSecondLastBusinessDayOfLastMonth } from "features/offers/helpers";
import usePermission from "common/security/usePermission";
import Permissions from "common/security/permissions";
import { FeeThin } from "domain/fee";
import AddFee from "features/offers/AddFee";
import useAjaxCall from "common/ui/useAjaxCall";
import { logHttpError, withCancellationToken } from "common/http/httpService";
import { getProducerAccountDisplayName, ProducerAccount } from "features/settings/producers/producerAccount";
import contractTypeHelper from "domain/contractTypeHelper";
import unitHelper from "domain/unitHelper";
import useTranslators from "common/translation/useTranslators";
import { isAgrisErp } from "domain/erpSystem";
import { getCommodityBalanceType } from "domain/commodityBalance";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { useAllowedPaymentOptions } from "features/merchants/usePaymentOptions";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import LocationPicker from "features/locations/LocationPicker";
import UiCustomization from "domain/uiCustomization";
import { FormField } from "domain/formFieldSetting";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import ContractExtendedType from "domain/contractExtendedType";
import { isDeferPayInterestEnabled } from "domain/merchant";
import useFeatureFlag from "features/featureFlags/useFeatureFlag";

export type EditContractDetailsProps = {
    contract: Contract;
    traders: Trader[];
    locations: PhysicalLocation[];
    deliveryMethods: DeliveryMethod[];
    deliveryPeriods: DeliveryPeriod[];
    futuresContracts: FuturesContract[];
    paymentOptions: PaymentOption[];
    contractExtendedTypes: ContractExtendedType[];
    canUseDetermineLater: boolean;
    canPushBasis: boolean;
    onContractUpdated(contract: Contract): void;
    isPriceUpdated: boolean;
    canEditContracts: boolean;
    fees: FeeThin[];
    uiCustomization: UiCustomization;
    editableFields: FormField[];
};

const EditContractDetails: React.FC<EditContractDetailsProps> = (props: EditContractDetailsProps) => {
    const customerWillSellText = useCustomerWillSellText();

    const [customerAccounts, setCustomerAccounts] = useState<ProducerAccount[]>([]);
    const [contractSubTypes, setContractSubTypes] = useState<ContractSubType[]>([]);
    const [isCashPriceSynced, setIsCashPriceSynced] = useState<boolean>(isCashContractType());
    const [showExtendedFeesPanel, setShowExtendedFeesPanel] = useState(false);

    const showSyncPriceControl: boolean = isCashContractType() && props.canEditContracts && !isFlatCashCommodity();
    const activeMerchant = useActiveMerchant();
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const allowDetermineLater: boolean = activeMerchant.isDetermineLaterAllowed;
    const regions = props.locations.filter(l => l.homeLocation == null);
    const canSupervisorEdit = usePermission(Permissions.SupervisorEditContract);
    const showLocalUOM = props.contract?.commodity?.baseUnit.id !== props.contract?.commodity?.localUnit.id;
    const unitsConversionRates = useUnitConversionRates();
    const isContractAppliedInAgris = isAgrisErp(activeMerchant.erpSystem) && getCommodityBalanceType(props.contract.deliveryMethod) != null;
    const availablePaymentOptionsDetails = useAllowedPaymentOptions(props.paymentOptions);
    const translators = useTranslators();
    const quantityPrecision = props.uiCustomization.addEditOfferContractQuantityDecimalPrecision;
    const isBushelOnly = props.contract.contractType === ContractType.BushelOnly;
    const shouldDisplayInterestStartDate = isDeferPayInterestEnabled(activeMerchant);
    const showDetermineLater = props.canUseDetermineLater && allowDetermineLater;
    const showPushBasisFeature = useFeatureFlag("ShowPushBasis");

    useAjaxCall(async cxt => {
        try {
            const result = await withCancellationToken(producerService, cxt).getProducer(props.contract.producer.id);

            setCustomerAccounts(result.accounts);
        } catch (error) {
            logHttpError("Unable to get customer", error);
        }
    }, []);

    useAjaxCall(async cxt => {
        try {
            if (props.contract == null) return;

            const result = await withCancellationToken(contractService, cxt).listContractSubTypes(
                props.contract.contractType,
                props.contract.customerWillSell,
                props.contract.deliveryPeriod?.cropYear.id,
                props.contract.paymentOption,
                props.contract.deliveryMethod);

            setContractSubTypes(result);
        } catch (error) {
            logHttpError("Unable to list contract subtypes", error);
        }
    }, [props.contract?.deliveryPeriod?.cropYear.id, props.contract?.paymentOption]);

    function onBasisChanged(value: number) {
        const newContract = {
            ...props.contract, basis: value, isBasisLocked: true,
            cashPrice: isCashPriceSynced ? NP.plus(value, props.contract.futuresPrice) : props.contract.cashPrice
        };
        props.onContractUpdated(newContract);
    }

    function canChangeBasis(): boolean {
        if (isCashPriceSynced)
            return true;
        if (props.contract.deliveryMethod === DeliveryMethod.PickedUp && props.contract.isBasisLocked)
            return true;
        if (props.canPushBasis && props.contract.isBasisLocked)
            return true;
        return false;
    }

    function getFinalPricingDate(futuresContract: FuturesContract): Date {
        if (isCashContractWithOpenStorage(props.contract)) {
            return props.contract?.deliveryPeriodEndDate;
        }

        if (isBasisContractType()) {
            return getSecondLastBusinessDayOfLastMonth(futuresContract?.expiration);
        }

        const contractFinalPricingDate = moment(props.contract?.finalPricingDate);
        const futuresContractExpirationDate = moment(futuresContract?.expiration || null).subtract(1, 'days');
        if (futuresContractExpirationDate.isValid()) {
            return futuresContractExpirationDate.toDate();
        }
        else if (contractFinalPricingDate.isValid()) {
            return contractFinalPricingDate.toDate();
        }
        return null;
    }

    function showFixBasisToggle() {
        if (isCashPriceSynced)
            return false;
        if (!(props.contract?.deliveryMethod === DeliveryMethod.PickedUp || props.canPushBasis))
            return false;
        if (showPushBasisToggle())
            return false;
        return true;
    }

    function showPushBasisToggle() {
        if (!showPushBasisFeature)
            return false;
        if (isCashPriceSynced)
            return false;
        if (!props.canPushBasis)
            return false;
        if ([ContractType.Basis, ContractType.Cash, ContractType.CashMinPrice].includes(props.contract?.contractType)) {
            return props.contract.customerWillSell;
        }
        return false;
    }

    function isCashContractType(): boolean {
        return contractTypeHelper.isCash(props.contract?.contractType);
    }

    function isHtaContractType(): boolean {
        return contractTypeHelper.isHta(props.contract?.contractType);
    }

    function isBasisContractType(): boolean {
        return props.contract?.contractType === ContractType.Basis;
    }

    function isDelayedPriceContractType(): boolean {
        return props.contract?.contractType === ContractType.DelayedPrice;
    }

    function isFlatCashCommodity(): boolean {
        return props.contract?.commodity?.isCashOnly;
    }

    function calculateBasis(value: number): number {
        if (isFlatCashCommodity()) {
            return 0;
        }
        else {
            return isCashPriceSynced ? NP.minus(value, props.contract.futuresPrice) : props.contract.basis;
        }
    }

    function shouldShowFreightFee() {
        return props.contract?.customerWillSell ?
            props.contract?.deliveryMethod === DeliveryMethod.PickedUp :
            props.contract?.deliveryMethod === DeliveryMethod.Delivered;
    }

    function showDeliveryTerms() {
        if (isDelayedPriceContractType())
            return false;
        if (isCashContractType() && props.contract.sourceContractId != null &&
            props.contract.sourceContractType !== ContractType.DelayedPrice)
            return false;
        if (!activeMerchant.generalSettings.displayDeliveryTermsForBuy && !props.contract?.customerWillSell)
            return false;
        if (!activeMerchant.generalSettings.displayDeliveryTermsForSell && props.contract?.customerWillSell)
            return false;
        return true;
    }

    function displayCustomerContractId() {
        if (!activeMerchant.generalSettings.displayCustomerContractIdForBuy && !props.contract?.customerWillSell)
            return false;
        if (!activeMerchant.generalSettings.displayCustomerContractIdForSell && props.contract?.customerWillSell)
            return false;
        return true;
    }

    function getMaxExpirationDate() {
        if (isCashContractWithOpenStorage(props.contract)) {
            return formatDate(toUtcDay(props.contract.deliveryPeriodEndDate), "yyyy-MM-DD");
        }
        else if (props.contract.futuresContract != null) {
            return formatDate(getPreviousDayForGivenDate(toUtcDay(props.contract.futuresContract?.expiration)), "yyyy-MM-DD");
        }
        else {
            return null;
        }
    }

    function shouldDisplayRegion() {
        return activeMerchant.generalSettings.displayRegion &&
            props.contract?.location?.homeLocation == null &&
            (props.contract?.location == null ||
                !props.locations.some(l => l.homeLocation?.id === props.contract?.location?.id)) &&
            regions?.length > 1;
    }

    function getRegion(l: PhysicalLocation) {
        if (activeMerchant.generalSettings.displayRegion) {
            if (l == null) {
                return regions?.length > 0 ? regions[0] : null;
            }
            else {
                return l.homeLocation != null ? getPhysicalLocation(l.homeLocation) : l;
            }
        }
        return null;
    }

    function getQuantityInLocal(value: number) {
        return unitHelper.getLocalQuantity(value, props.contract.commodity, unitsConversionRates, quantityPrecision);
    }

    function getPriceInLocal(value: number) {
        return unitHelper.getLocalPrice(value, props.contract.commodity, unitsConversionRates);
    }

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.contract.contractCurrency.code, props.contract.commodity, unitsConversionRates);
    }

    function getFeesText() {
        const fees = getTotalFees(props.contract);
        return getPriceText(fees);
    }

    function shouldDisableQuantityField() {
        return !props.canEditContracts || !props.contract.canEditQuantity ||
            (!(props.contract.isSupervisorEditing && canSupervisorEdit) && isContractAppliedInAgris);
    }

    return props.contract == null
        ? <></>
        : <>
            {isBushelOnly && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_ExtendedType")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.contract.contractExtendedType}
                        options={props.contractExtendedTypes}
                        includeInvalidOption
                        showNullRow
                        required
                        disabled
                        valueGetter={m => m}
                        textGetter={m => m.name}
                        onValueSelected={m => props.onContractUpdated({ ...props.contract, contractExtendedType: m })}
                    />
                </Col>
            </Form.Group>}

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Customer")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getProducerDisplayName(props.contract.producer)}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column className="optional">{localize("Details_Label_AccountId")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.CustomerAccount)}
                        options={customerAccounts}
                        showNullRow={!customerAccounts || props.contract.customerAccount == null}
                        value={props.contract.customerAccount}
                        valueGetter={m => m.accountId}
                        textGetter={m => getProducerAccountDisplayName(m)}
                        onValueSelected={m => props.onContractUpdated({ ...props.contract, customerAccount: m })} />
                </Col>
            </Form.Group>

            {activeMerchant.generalSettings.displayErpContractId && !isCustomer && props.contract.sourceContractId == null &&
                <Form.Group as={Row}>
                    <Form.Label column className="optional">{localize("Details_Label_ErpContractId")}</Form.Label>
                    <Col xs="8">
                        <Form.Control
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.ErpContractId)}
                            value={props.contract.erpContractId}
                            onChange={ev => props.onContractUpdated({ ...props.contract, erpContractId: ev.target.value })}>
                        </Form.Control>
                    </Col>
            </Form.Group>}

            <Form.Group as={Row}>
                <Form.Label column>{customerWillSellText.label}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{customerWillSellText.formatValue(props.contract.customerWillSell)}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Commodity")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.contract.commodity.name}</div>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Location")}</Form.Label>
                <Col xs="8">
                    <LocationPicker
                        disabled={!props.canEditContracts || (activeMerchant.hasErpIntegration && !props.editableFields.includes(FormField.DeliveryLocation) &&
                            !(props.contract.isSupervisorEditing && canSupervisorEdit))}
                        required={!showDetermineLater}
                        locations={props.locations.filter(m => m.isDeliveryLocation)}
                        showDetermineLater={showDetermineLater}
                        selected={props.contract.location}
                        onLocationSelected={l => props.onContractUpdated({ ...props.contract, location: l, region: getRegion(l) })}
                    />
                </Col>
            </Form.Group>

            {activeMerchant.generalSettings.displayInventoryLocation && !isCustomer && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_InventoryLocation")}</Form.Label>
                <Col xs="8">
                    <LocationPicker
                        disabled={!props.canEditContracts || (activeMerchant.hasErpIntegration && !props.editableFields.includes(FormField.InventoryLocation))}
                        required={!showDetermineLater}
                        locations={props.locations.filter(m => m.isInventoryLocation)}
                        showDetermineLater={showDetermineLater}
                        selected={props.contract.inventoryLocation}
                        onLocationSelected={l => props.onContractUpdated({ ...props.contract, inventoryLocation: l })}
                    />
                </Col>
            </Form.Group>}

            {activeMerchant.generalSettings.displayContractLocation && !isCustomer && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_ContractLocation")}</Form.Label>
                <Col xs="8">
                    <LocationPicker
                        disabled={!props.canEditContracts || (activeMerchant.hasErpIntegration && !props.editableFields.includes(FormField.ContractLocation))}
                        required={!showDetermineLater}
                        locations={props.locations.filter(m => m.isContractLocation)}
                        showDetermineLater={showDetermineLater}
                        selected={props.contract.contractLocation}
                        onLocationSelected={l => props.onContractUpdated({ ...props.contract, contractLocation: l })}
                    />
                </Col>
            </Form.Group>}

            {shouldDisplayRegion() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Region")}</Form.Label>
                <Col xs="8">
                    <LocationPicker
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.Region)}
                        required
                        locations={regions}
                        selected={props.contract.region}
                        onLocationSelected={l => { props.onContractUpdated({ ...props.contract, region: l }) }}
                    />
                </Col>
            </Form.Group>}

            {!isBushelOnly && !isDelayedPriceContractType() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_DeliveryPeriod")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.DeliveryPeriod)}
                        value={props.contract.deliveryPeriod}
                        options={props.deliveryPeriods}
                        valueGetter={dp => dp.id}
                        textGetter={dp => getDeliveryPeriodDisplayName(dp)}
                        onValueSelected={dp => {
                            props.onContractUpdated({
                                ...props.contract, deliveryPeriod: dp, futuresContract: dp?.contract,
                                deliveryPeriodStartDate: dp?.start, deliveryPeriodEndDate: dp?.end,
                                finalPricingDate: getFinalPricingDate(dp?.contract)
                            })
                        }} />
                </Col>
            </Form.Group>}

            {(!isDelayedPriceContractType() || activeMerchant.hasErpIntegration) && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_DeliveryMethod")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        disabled={!props.canEditContracts || isContractAppliedInAgris || !props.editableFields.includes(FormField.DeliveryMethod)}
                        value={props.contract.deliveryMethod}
                        options={props.deliveryMethods}
                        showNullRow
                        required
                        valueGetter={m => m}
                        textGetter={m => translators.deliveryMethods.translate(m)}
                        onValueSelected={m => {
                            props.onContractUpdated({ ...props.contract, deliveryMethod: m });
                        }} />
                </Col>
            </Form.Group>}

            {shouldShowFreightFee() && <Form.Group as={Row}>
                <Form.Label column className="optional">{localize("Details_Label_FreightFee")}</Form.Label>
                <Col xs="8">
                    <NumberEditor className='form-control'
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.FreightFee)}
                        min={0}
                        step={.01}
                        allowDecimals
                        precision={4}
                        enforcePrecision
                        value={props.contract.freightFee}
                        onValueChange={value => { props.onContractUpdated({ ...props.contract, freightFee: value }); }}
                    />
                </Col>
            </Form.Group>}

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_StartDate")}</Form.Label>
                <Col xs="8">
                    {props.canPushBasis ?
                        <Form.Control type="date"
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.StartDate)}
                            value={formatUTCDate(props.contract.deliveryPeriodStartDate, "yyyy-MM-DD")}
                            required
                            max={formatUTCDate(props.contract.deliveryPeriodEndDate, "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onContractUpdated({ ...props.contract, deliveryPeriodStartDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                        </Form.Control> :
                        <div className="col-form-label">{formatUTCDate(props.contract.deliveryPeriodStartDate, "yyyy-MM-DD")}</div>
                    }
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_EndDate")}</Form.Label>
                <Col xs="8">
                    {props.canPushBasis ?
                        <Form.Control type="date"
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.EndDate)}
                            value={formatUTCDate(props.contract.deliveryPeriodEndDate, "yyyy-MM-DD")}
                            required
                            min={formatUTCDate(props.contract.deliveryPeriodStartDate, "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onContractUpdated({ ...props.contract, deliveryPeriodEndDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                        </Form.Control> :
                        <div className="col-form-label">{formatUTCDate(props.contract.deliveryPeriodEndDate, "yyyy-MM-DD")}</div>
                    }
                </Col>
            </Form.Group>

            <Form.Group as={Row} className={!showExtendedFeesPanel && props.contract.fees.length > 0 && "mb-0"}>
                {props.fees.length > 0 && <Form.Label column className="text-left">
                    <span onClick={() => setShowExtendedFeesPanel(!showExtendedFeesPanel)} aria-controls="fees-collapse" aria-expanded={showExtendedFeesPanel}>
                        <i className="material-icons">{showExtendedFeesPanel ? 'expand_less' : 'expand_more'}</i>
                    </span>
                </Form.Label>}
                <Form.Label column className="pl-0">{localize("Details_Label_TotalFees")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getFeesText()}</div>
                </Col>
            </Form.Group>

            <Collapse in={showExtendedFeesPanel} className={"border-top border-bottom pt-2 pb-1 mb-2"}>
                <div id="fees-collapse">
                    <AddFee fees={props.fees}
                        selectedFees={props.contract?.fees}
                        date={props.contract.deliveryPeriodStartDate}
                        disabled={isCustomer || !props.editableFields.includes(FormField.TotalFees)}
                        onFeesChanged={(fees) => props.onContractUpdated({ ...props.contract, fees: fees })}
                    />
                </div>
            </Collapse>

            {!isDelayedPriceContractType() && !isFlatCashCommodity() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Symbol")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        disabled={!props.editableFields.includes(FormField.Symbol) || (props.canEditContracts ? !(props.contract.location == null || props.canPushBasis) : true)}
                        showNullRow={props.canPushBasis}
                        nullRowText={localizeCommon("Value_Select")}
                        required
                        value={props.contract.futuresContract}
                        options={props.futuresContracts}
                        valueGetter={fc => fc.id}
                        textGetter={fc => fc.symbol}
                        onValueSelected={fc => {
                            props.onContractUpdated({ ...props.contract, futuresContract: fc, finalPricingDate: getFinalPricingDate(fc) })
                        }} />
                </Col>
            </Form.Group>}

            {!(isHtaContractType() || isDelayedPriceContractType()) && !isFlatCashCommodity() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Basis")}</Form.Label>
                <Col xs="4">
                    <NumberEditor className="form-control"
                        disabled={!props.editableFields.includes(FormField.Basis) || (props.canEditContracts ? !canChangeBasis() : true)}
                        required={props.contract.isBasisLocked}
                        step={.01}
                        allowDecimals
                        precision={5}
                        enforcePrecision
                        value={props.contract.basis ?? ''}
                        onValueChange={onBasisChanged}
                        placeholder='--'
                    />
                </Col>
                {showSyncPriceControl && <div className="sync-price-top sync-price"></div>}
                <Col xs="4">
                    {showFixBasisToggle() && <>
                        <Form.Label>{localize("Details_Label_SetBasis")}</Form.Label>&nbsp;
                        <Form.Check type="switch" 
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.Basis)} 
                            id="fix-basis" 
                            label="" 
                            className="d-inline-block" 
                            checked={props.contract.isBasisLocked}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onContractUpdated({ ...props.contract, isBasisLocked: ev.target.checked })} />
                    </>}
                    {showPushBasisToggle() && <>
                        <Form.Label>{localize("Details_Label_PushBasis")}</Form.Label>&nbsp;
                        <Form.Check type="switch" 
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.Basis)} 
                            className="d-inline-block" 
                            checked={props.contract.isBasisPushed}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onContractUpdated({ ...props.contract, isBasisPushed: ev.target.checked })} />
                    </>}
                </Col>
            </Form.Group>}

            {props.contract.isBasisPushed && 
                <Form.Group as={Row} data-testid="push-basis-value-row">
                    <Form.Label column>{localize("Details_Label_Push_Value")}</Form.Label>
                    <Col xs="4">
                        <NumberEditor
                            className="form-control"
                            required={props.contract.isBasisPushed}
                            step={.01}
                            allowDecimals
                            min={0}
                            precision={5}
                            enforcePrecision
                            value={props.contract.pushedBasis ?? ''}
                            onValueChange={value => props.onContractUpdated({ ...props.contract, pushedBasis: value })}
                            placeholder={'--'}
                        />
                    </Col>
                        <Col xs="4" className="p-0">
                        <Form.Label>{localize("Details_Label_Actual_Basis")}</Form.Label>&nbsp;
                        <div className="col-form-label d-inline-block">
                            {formatNumber(NP.plus(props.contract.basis ?? 0, props.contract.pushedBasis ?? 0), "0,0.0000")}
                        </div>
                    </Col>
                </Form.Group>
            }

            {!(isBasisContractType() || isDelayedPriceContractType()) && <Form.Group as={Row}>
                <Form.Label column>{getPriceLabelForContract(props.contract.contractType)}</Form.Label>
                <Col xs="4">
                    {isCashContractType() &&
                        <NumberEditor className={props.isPriceUpdated ? 'form-control font-weight-bold' : 'form-control'}
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.CashPrice)}
                            allowDecimals
                            precision={5}
                            enforcePrecision
                            roundingCent
                            min={0}
                            step={.01}
                            value={props.contract.cashPrice}
                            unit={props.contract.contractCurrency.code}
                            onValueChange={value => props.onContractUpdated({
                                ...props.contract, cashPrice: value,
                                basis: calculateBasis(value)
                            })}
                        />
                    }
                    {(isBushelOnly || isHtaContractType()) &&
                        <NumberEditor className={props.isPriceUpdated ? 'form-control font-weight-bold' : 'form-control'}
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.FuturesPrice)}
                            allowDecimals
                            precision={5}
                            enforcePrecision
                            roundingCent
                            min={0}
                            step={.01}
                            value={props.contract.futuresPrice}
                            unit={props.contract.contractCurrency.code}
                            onValueChange={value => props.onContractUpdated({ ...props.contract, futuresPrice: value })}
                        />
                    }
                </Col>
                {showSyncPriceControl && <div className="sync-price-bottom sync-price"></div>}
                <Col xs="4" className={showSyncPriceControl ? "sync-price-control" : ""}>
                    {showSyncPriceControl && <>
                        <Form.Check type="switch" id="sync-price" label={localize("Details_Label_SyncPrices")} className="d-inline-block" checked={isCashPriceSynced}
                            onChange={() => setIsCashPriceSynced(!isCashPriceSynced)} />
                    </>}
                </Col>
            </Form.Group>}

            {showLocalUOM && isCashContractType() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_LocalCashPrice")}</Form.Label>
                <Col xs="4">
                    <NumberEditor className={props.isPriceUpdated ? 'form-control font-weight-bold' : 'form-control'}
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.CashPrice)}
                        allowDecimals
                        precision={5}
                        enforcePrecision
                        roundingCent
                        min={0}
                        value={getPriceInLocal(props.contract.cashPrice)}
                        unit={props.contract.contractCurrency.code}
                        onValueChange={value => {
                            const priceInBase = unitHelper.getBasePrice(value, props.contract.commodity, unitsConversionRates);
                            props.onContractUpdated({
                                ...props.contract, cashPrice: priceInBase,
                                basis: isCashPriceSynced ? NP.minus(priceInBase, props.contract.futuresPrice) : props.contract.basis
                            })
                        }}
                    />
                </Col>
                <Col xs="4"></Col>
            </Form.Group>}

            {!(isBasisContractType() || isDelayedPriceContractType()) && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_FinalPrice")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getPriceText(props.contract.finalPrice)}</div>
                </Col>
            </Form.Group>}

            <Form.Group as={Row}>
                <Form.Label column>{isBasisContractType() ? localize("Details_Label_ContractedQuantity") : localize("Details_Label_Quantity")}</Form.Label>
                {!showLocalUOM && <Col xs="8">
                    {isBasisContractType() ? <div className="col-form-label">{props.contract.quantityContracted} {props.contract.commodity.localUnit.abbreviation}</div>
                        : <NumberEditor className="form-control"
                            aria-label="Quantity"
                            required
                            allowDecimals
                            precision={quantityPrecision}
                            thousandSeparator
                            unit={props.contract.commodity.baseUnit.abbreviation}
                            min={0}
                            value={props.contract.quantityContracted}
                            disabled={shouldDisableQuantityField() || !props.editableFields.includes(FormField.Quantity)}
                            onValueChange={value => props.onContractUpdated({
                                ...props.contract, quantityContracted: value
                            })}
                        />}
                </Col>}
                {showLocalUOM && <>
                    {isBasisContractType() ?
                        <Col xs="8">
                            <div className="col-form-label">{unitHelper.getQuantityTextWithUnits(props.contract.quantityContracted, props.contract.commodity, unitsConversionRates, quantityPrecision)}</div>
                        </Col>
                        :
                        <><Col xs="4">
                            <NumberEditor className="form-control"
                                aria-label="Quantity"
                                required
                                allowDecimals
                                precision={quantityPrecision}
                                thousandSeparator
                                unit={props.contract.commodity.baseUnit.abbreviation}
                                min={0}
                                value={props.contract.quantityContracted}
                                disabled={shouldDisableQuantityField() || !props.editableFields.includes(FormField.Quantity)}
                                onValueChange={value => props.onContractUpdated({
                                    ...props.contract, quantityContracted: value
                                })}
                            />
                        </Col>
                            <Col xs="4" className="pl-0">
                                <NumberEditor className="form-control"
                                    aria-label="Quantity Local"
                                    required
                                    allowDecimals
                                    precision={quantityPrecision}
                                    thousandSeparator
                                    unit={props.contract.commodity.localUnit.abbreviation}
                                    min={0}
                                    value={getQuantityInLocal(props.contract.quantityContracted)}
                                    disabled={shouldDisableQuantityField() || !props.editableFields.includes(FormField.Quantity)}
                                    onValueChange={value => {
                                        const quantityInBase = unitHelper.getBaseQuantity(value, props.contract.commodity, unitsConversionRates, quantityPrecision);
                                        props.onContractUpdated({ ...props.contract, quantityContracted: quantityInBase })
                                    }}
                                />
                            </Col></>
                    }
                </>}
            </Form.Group>

            {isBasisContractType() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_UnpricedQuantity")}</Form.Label>
                <Col xs={showLocalUOM ? "4" : "8"}>
                    <NumberEditor className="form-control"
                        aria-label="Unpriced Quantity"
                        required
                        allowDecimals
                        precision={quantityPrecision}
                        thousandSeparator
                        unit={props.contract.commodity.baseUnit.abbreviation}
                        min={0}
                        value={props.contract.remainingQuantity}
                        disabled={shouldDisableQuantityField() || !props.editableFields.includes(FormField.Quantity)}
                        onValueChange={value => props.onContractUpdated({
                            ...props.contract, remainingQuantity: value, quantityContracted: NP.minus(props.contract.quantityContracted, NP.minus(props.contract.remainingQuantity, value))
                        })}
                    />
                </Col>
                {showLocalUOM && <Col xs="4">
                    <NumberEditor className="form-control"
                        aria-label="Unpriced Quantity Local"
                        required
                        allowDecimals
                        precision={quantityPrecision}
                        thousandSeparator
                        unit={props.contract.commodity.localUnit.abbreviation}
                        min={0}
                        value={getQuantityInLocal(props.contract.remainingQuantity)}
                        disabled={shouldDisableQuantityField() || !props.editableFields.includes(FormField.Quantity)}
                        onValueChange={value => {
                            const quantityInBase = unitHelper.getBaseQuantity(value, props.contract.commodity, unitsConversionRates, quantityPrecision);
                            props.onContractUpdated({
                                ...props.contract, remainingQuantity: quantityInBase, quantityContracted: NP.minus(props.contract.quantityContracted, NP.minus(props.contract.remainingQuantity, quantityInBase))
                            })
                        }}
                    />
                </Col>}
            </Form.Group>}

            {!isCashContractType() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_FinalPricingDate")}</Form.Label>
                <Col xs="8">
                    <Form.Control type="date"
                        disabled={!props.canEditContracts || !props.editableFields.includes(FormField.ExpirationDate)}
                        value={formatUTCDate(props.contract.finalPricingDate, "yyyy-MM-DD")}
                        required
                        max={getMaxExpirationDate()}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onContractUpdated({ ...props.contract, finalPricingDate: moment.utc(ev.target.value).toDate() })}>
                    </Form.Control>
                </Col>
            </Form.Group>
            }

            {!isCustomer &&
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_Trader")}</Form.Label>
                    <Col xs="8">
                        <TypedSelect
                            disabled={!props.canEditContracts || !props.editableFields.includes(FormField.Trader)}
                            value={props.contract.trader}
                            required
                            options={props.traders}
                            valueGetter={m => m.userId}
                            textGetter={m => `${m.firstName} ${m.lastName}`}
                            onValueSelected={m => props.onContractUpdated({ ...props.contract, trader: m })} />
                    </Col>
                </Form.Group>
            }

            {!isBushelOnly && activeMerchant.generalSettings.displayPaymentOption && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_PaymentOption")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.contract.paymentOption}
                        disabled={!props.editableFields.includes(FormField.PaymentOption)}
                        options={availablePaymentOptionsDetails}
                        valueGetter={p => p?.paymentOption ?? p}
                        textGetter={p => p.displayName}
                        onValueSelected={p => props.onContractUpdated({
                            ...props.contract,
                            paymentOption: p.paymentOption,
                            deferredPayDate: p.paymentOption !== PaymentOption.DeferredPay ? null : firstBusinessDayOfNextYear(),
                            interestRate: p.paymentOption === PaymentOption.DeferredPay && activeMerchant.generalSettings.defaultDeferPayInterestRate != null ? activeMerchant.generalSettings.defaultDeferPayInterestRate : null,
                            interestStartDate: p.paymentOption === PaymentOption.DeferredPay && shouldDisplayInterestStartDate ? moment().format('yyyy-MM-DD') : null
                        })} />
                </Col>
            </Form.Group>}

            {contractSubTypes.length > 1 && props.contract.deliveryMethod !== DeliveryMethod.DelayedPrice && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_ContractSubType")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={{ value: props.contract.contractSubType }}
                        options={contractSubTypes}
                        valueGetter={p => p.value}
                        textGetter={p => p.name}
                        onValueSelected={p => props.onContractUpdated({ ...props.contract, contractSubType: p.value })} />
                </Col>
            </Form.Group>}

            {props.contract.paymentOption === PaymentOption.DeferredPay && <div>
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_PayDate")}</Form.Label>
                    <Col xs="8">
                        <Form.Control type="date"
                            value={props.contract.deferredPayDate}
                            required
                            min={formatDate(getNextDay(), "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onContractUpdated({ ...props.contract, deferredPayDate: ev.target.value })}>
                        </Form.Control>
                    </Col>
                </Form.Group>

                {shouldDisplayInterestStartDate && <Form.Group as={Row}>
                    <Form.Label column className="optional">{localize("Details_Label_InterestStartDate")}</Form.Label>
                    <Col xs="8">
                        <Form.Control type="date"
                            value={props.contract.interestStartDate}
                            min={formatTodaysDate('yyyy-MM-DD')}
                            max={props.contract.deferredPayDate}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onContractUpdated({ ...props.contract, interestStartDate: ev.target.value })}>
                        </Form.Control>
                    </Col>
                </Form.Group>}

                <Form.Group as={Row}>
                    <Form.Label column className="optional">{localize("Details_Label_InterestRate")}</Form.Label>
                    <Col xs="8">
                        {activeMerchant.generalSettings.defaultDeferPayInterestRate != null ?
                            <div className="col-form-label">{activeMerchant.generalSettings.defaultDeferPayInterestRate} %</div>
                            : <InputGroup className="rate-input">
                                <NumberEditor className="form-control"
                                    aria-label="InterestRate"
                                    disabled={!props.editableFields.includes(FormField.DeferPayInterestRate)}
                                    allowDecimals
                                    precision={4}
                                    min={0}
                                    max={100}
                                    placeholder={"0.0000"}
                                    value={props.contract.interestRate}
                                    onValueChange={value => props.onContractUpdated({ ...props.contract, interestRate: value })}
                                />
                                <InputGroup.Append className="rate-text">
                                    <InputGroup.Text>%</InputGroup.Text>
                                </InputGroup.Append>
                            </InputGroup>}
                    </Col>
                </Form.Group>
            </div>}

            <Form.Group as={Row}>
                <Form.Label column className="optional">{localize("Details_Label_Comment")}</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.contract.comments}
                        disabled={!props.editableFields.includes(FormField.Comment)}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onContractUpdated({ ...props.contract, comments: ev.target.value })}>
                    </Form.Control>
                </Col>
            </Form.Group>

            {showDeliveryTerms() && <Form.Group as={Row}>
                <Form.Label column className="optional">{localize("Details_Label_DeliveryTerms")}</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.contract.deliveryTerms}
                        disabled={!props.editableFields.includes(FormField.DeliveryTerms)}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onContractUpdated({ ...props.contract, deliveryTerms: ev.target.value })}>
                    </Form.Control>
                </Col>
            </Form.Group>}

            {displayCustomerContractId() && <Form.Group as={Row}>
                <Form.Label column className="optional">{localize("Details_Label_CustomerContractId")}</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.contract.customerContractId}
                        disabled={!props.editableFields.includes(FormField.CustomerContractId)}
                        required={activeMerchant.generalSettings.makeCustomerContractIdRequired}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onContractUpdated({ ...props.contract, customerContractId: ev.target.value })}>
                    </Form.Control>
                </Col>
            </Form.Group>}
        </>;
}

export default EditContractDetails;